import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api.config.jsx';
import { trackEvent, EVENT_TYPE_PAGE } from '../utils/trackEvent.jsx';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Phone } from "lucide-react";

const PartnerSignUp = () => {
  useEffect(() => {
    trackEvent(EVENT_TYPE_PAGE);
    console.log('Partner signup page loaded');
  }, []);

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    accountType: 'partner',
    partnerName: '',
    partnerDescription: '',
    websiteUrl: '',
    partnerType: 'lender',
    offersIslamicFinance: true,
    interestedProductCategories: [],
    existingProducts: '' // Stored as raw string; parsed to array on submit
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateMobileNumber = (number) => {
    const regex = /^[0-9]{10}$/;
    return regex.test(number);
  };


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleMultiSelectChange = (name, value) => {
    setFormData(prev => {
      const currentValues = prev[name] || [];
      const isSelected = currentValues.includes(value);
      return {
        ...prev,
        [name]: isSelected
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value]
      };
    });
  };

  const handleArrayInputChange = (name, value) => {
    // Store raw value to allow all characters (space, comma, etc.)
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!validateMobileNumber(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Enter a valid 10-digit mobile number';
    }

    if (formData.interestedProductCategories.length === 0) {
      newErrors.interestedProductCategories = 'Select at least one product';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const signupResponse = await fetch(`${getApiUrl()}/auth/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          mobile: formData.mobileNumber,
          password: '',
          salt: '',
          clientHash: false,
          partnerInfo: {
            partnerName: formData.partnerName,
            partnerDescription: formData.partnerDescription,
            websiteUrl: formData.websiteUrl,
            partnerType: formData.partnerType,
            offersIslamicFinance: formData.offersIslamicFinance,
            interestedProductCategories: formData.interestedProductCategories,
            existingProducts: formData.existingProducts
              .split(',')
              .map((item) => item.trim())
              .filter((item) => item.length > 0)
          }
        })
      });

      const signupData = await signupResponse.json();

      if (!signupResponse.ok) {
        throw new Error(signupData.message || 'Signup failed');
      }
      const partnerEmail = formData.email;
      // Show success message and redirect to signin
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        mobileNumber: '',
        accountType: 'partner',
        partnerName: '',
        partnerDescription: '',
        websiteUrl: '',
        partnerType: 'lender',
        offersIslamicFinance: true,
        interestedProductCategories: [],
        existingProducts: '' // Stored as raw string; parsed to array on submit
      });

      alert('Signup complete! Check your email for your password, then sign in.');

      try {
        const setPasswordResponse = await fetch(`${getApiUrl()}/auth/auth/set-password-by-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: partnerEmail }),
        });
        if (!setPasswordResponse.ok) {
          const errorData = await setPasswordResponse.json().catch(() => ({}));
          alert(errorData.message || 'Failed to send password to your email. Please contact support.');
        }
      } catch (setPasswordError) {
        alert('Error sending password to email. Please contact support.');
        console.error('Set password email error:', setPasswordError);
      }

      navigate('/partner-signin');
    } catch (error) {
      console.error('Partner signup error:', error);
      setErrors({
        submit: error.message || 'Something went wrong. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#f8fafc] to-[#e2e8f0] py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <h2 className="text-3xl font-bold text-[#1a4a1a]">🤝 Create your partner account</h2>
          <p className="text-[#1a4a1a]/80">
            Enter your institution details below. We'll create an account and email your password to start the HalalCred onboarding process.
          </p>
        </CardHeader>
        <CardContent>
          {errors.submit && (
            <div className="mb-4 p-3 text-sm text-red-500 bg-red-50 rounded-md">
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Personal Information */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobileNumber">Mobile Number *</Label>
              <Input
                id="mobileNumber"
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                required
                disabled={isLoading}
                placeholder="10-digit number"
              />
              {errors.mobileNumber && (
                <p className="text-sm text-red-500">{errors.mobileNumber}</p>
              )}
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-[#1a4a1a] mb-4">Your institution</h3>
            </div>

            <div className="space-y-2">
              <Label htmlFor="partnerName">Institution name *</Label>
              <Input
                id="partnerName"
                type="text"
                name="partnerName"
                value={formData.partnerName}
                onChange={handleChange}
                required
                disabled={isLoading}
                placeholder="e.g., Craft3"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="partnerDescription">About your institution *</Label>
              <Textarea
                id="partnerDescription"
                name="partnerDescription"
                value={formData.partnerDescription}
                onChange={handleChange}
                required
                disabled={isLoading}
                placeholder="Who you serve, what you offer, and what you want to launch"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="websiteUrl">Website URL</Label>
              <Input
                id="websiteUrl"
                type="text"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="https://example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="partnerType">Institution type *</Label>
              <Select
                value={formData.partnerType}
                onValueChange={(value) => handleSelectChange('partnerType', value)}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lender">Lender</SelectItem>
                  <SelectItem value="service_provider">Service Provider</SelectItem>
                  <SelectItem value="technology_partner">Technology Partner</SelectItem>
                  <SelectItem value="consulting_partner">Consulting Partner</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="offersIslamicFinance"
                  checked={formData.offersIslamicFinance}
                  onChange={(e) => handleCheckboxChange('offersIslamicFinance', e.target.checked)}
                  disabled={isLoading}
                  className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                />
                <Label htmlFor="offersIslamicFinance" className="cursor-pointer">
                  We already offer Islamic financing products
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Products you want to offer *</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 border rounded-lg">
                {[
                  'Working Capital',
                  //'Ijara',
                  //'Murabaha',
                  'Commercial Real Estate',
                  //'Mudaraba',
                  //'Salam',
                  //'Istisna',
                  //'Tawarruq'
                ].map((category) => (
                  <div key={category} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`category-${category}`}
                      checked={formData.interestedProductCategories.includes(category)}
                      onChange={() => handleMultiSelectChange('interestedProductCategories', category)}
                      disabled={isLoading}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <Label htmlFor={`category-${category}`} className="cursor-pointer text-sm">
                      {category}
                    </Label>
                  </div>
                ))}
              </div>
              {errors.interestedProductCategories && (
                <p className="text-sm text-red-500">Select at least one product</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="existingProducts">Current products (if any)</Label>
              <Textarea
                id="existingProducts"
                name="existingProducts"
                value={formData.existingProducts}
                onChange={(e) => handleArrayInputChange('existingProducts', e.target.value)}
                disabled={isLoading}
                placeholder="e.g., Home Loans, Commercial Financing (comma-separated)"
                rows={2}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> We'll email your password after you sign up.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 font-semibold py-3 rounded-full transition-all duration-200 transform hover:scale-105 shadow-lg"
              disabled={isLoading}
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col items-center gap-4">
          <p className="text-[#1a4a1a]/70">
            Already have an account?{' '}
            <Link to="/partner-signin" className="text-emerald-600 hover:underline font-semibold">
              Sign In
            </Link>
          </p>
          
          {/* Contact Us Section */}
          <div className="border-t pt-4 w-full">
            <p className="text-sm text-[#1a4a1a]/70 mb-3 text-center">Have questions? We're here to help</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="mailto:salaam@halalcred.com?subject=Partner Signup Inquiry"
                className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>salaam@halalcred.com</span>
              </a>
              <span className="hidden sm:inline text-[#1a4a1a]/40">|</span>
              <a
                href="tel:+14253400980"
                className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>+1-425-340-0980</span>
              </a>
            </div>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default PartnerSignUp; 